import React, { useEffect, useMemo, useState } from "react";
import { Search, Users, UserCog, Building2, GitBranch } from "lucide-react";
import { get } from "../../../services/lewooApi";
import { StatCard, PageHeader, LoadingScreen } from "../../../components/admin/EssUi";
import EmployeeAvatar from "../../../components/admin/EmployeeAvatar";

const GREEN = "#225f2b";
const BORDER = "#e2e7e2";

const getInitials = (n) => (n || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();

const OrgNode = ({ emp, employees, depth, activeId, onSelect }) => {
  const children = employees.filter((e) => e.parent_id && e.parent_id === emp.id);
  const isActive = activeId === emp.id;

  return (
    <div>
      <div
        className="flex items-center gap-3 p-3 my-1 rounded-md cursor-pointer transition-colors"
        style={{
          marginLeft: depth * 32,
          background: isActive ? "#f0f7f0" : "white",
          border: `1px solid ${isActive ? GREEN : BORDER}`,
          maxWidth: 360,
        }}
        onClick={() => onSelect(emp)}
      >
        <EmployeeAvatar employee={emp} size={36} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-900 truncate">{emp.name}</p>
          <p className="text-xs text-slate-500 truncate">{emp.job_title || "—"}</p>
        </div>
        <span className="text-xs font-medium text-slate-400 flex-shrink-0">
          #{String(emp.id).padStart(4, "0")}
        </span>
      </div>
      {children.length > 0 && (
        <div className="ml-4 pl-4 border-l-2" style={{ borderColor: "#e2e7e2" }}>
          {children.map((c) => (
            <OrgNode key={c.id} emp={c} employees={employees} depth={depth + 1} activeId={activeId} onSelect={onSelect} />
          ))}
        </div>
      )}
    </div>
  );
};

const AdminPanelOrgPage = () => {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [activeEmp, setActiveEmp] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const [empRes, deptRes] = await Promise.all([
          get("/api/employees?limit=500"),
          get("/api/departments"),
        ]);
        setEmployees(empRes?.data || []);
        setDepartments(deptRes?.data || deptRes || []);
      } catch {
        setEmployees([]);
        setDepartments([]);
      }
      setLoaded(true);
    };
    load();
  }, []);

  const activeEmps = employees.filter((e) => e.active !== false && e.id != null);
  const roots = useMemo(() => activeEmps.filter((e) => !e.parent_id), [activeEmps]);

  const reachable = useMemo(() => {
    const set = new Set();
    const walk = (id) => {
      if (set.has(id)) return;
      set.add(id);
      activeEmps.forEach((e) => { if (e.parent_id === id) walk(e.id); });
    };
    roots.forEach((r) => walk(r.id));
    return set;
  }, [roots, activeEmps]);

  const orphanRoots = useMemo(() => {
    const orphans = activeEmps.filter((e) => !reachable.has(e.id));
    const orphanIds = new Set(orphans.map((e) => e.id));
    return orphans.filter((o) => !o.parent_id || !orphanIds.has(o.parent_id));
  }, [activeEmps, reachable]);

  const buildChain = (emp) => {
    const chain = [emp];
    let current = emp;
    while (current.parent_id) {
      const parent = employees.find((e) => e.id === current.parent_id);
      if (parent) { chain.unshift(parent); current = parent; }
      else break;
    }
    return chain;
  };

  const chain = activeEmp ? buildChain(activeEmp) : [];

  const filteredEmps = activeEmps.filter((e) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (e.name || "").toLowerCase().includes(q) || (e.job_title || "").toLowerCase().includes(q);
  });

  if (!loaded) return <LoadingScreen />;

  return (
    <div className="animate-fade-in max-w-7xl mx-auto">
      <PageHeader
        title="Org Chart"
        subtitle="Organizational hierarchy & team structure"
        actions={
          <div className="relative">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search employee..."
              className="ess-inp ess-si-inp"
              style={{ paddingLeft: 32, minWidth: 220 }}
            />
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#94a3b8" }} />
          </div>
        }
      />

      <div className="ess-sg ess-sg4" style={{ marginBottom: 20 }}>
        <StatCard label="Total Employees" value={String(activeEmps.length)} icon={Users} />
        <StatCard label="Managers" value={String(roots.length)} icon={UserCog} />
        <StatCard label="Departments" value={departments.length} icon={Building2} />
        <StatCard label="With Reports" value={String(activeEmps.filter((e) => activeEmps.some((c) => c.parent_id && c.parent_id === e.id)).length)} icon={GitBranch} />
      </div>

      {activeEmp && (
        <div className="mb-5" style={{ background: "#f8fafc", border: `1px solid ${BORDER}`, borderRadius: 8 }}>
          <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: BORDER }}>
            <div className="flex items-center gap-3">
              <EmployeeAvatar employee={activeEmp} size={36} />
              <div>
                <p className="text-sm font-semibold text-slate-900">{activeEmp.name}</p>
                <p className="text-xs text-slate-500">{activeEmp.job_title || "—"}</p>
              </div>
            </div>
            <button onClick={() => setActiveEmp(null)} className="ess-btn ess-btn-secondary">✕ Clear</button>
          </div>
          <div className="px-5 py-4">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3">Reporting Chain</p>
            <div className="flex items-center gap-1 flex-wrap">
              {chain.map((c, i) => (
                <React.Fragment key={c.id}>
                  <div
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md cursor-pointer transition-colors"
                    style={{
                      background: c.id === activeEmp.id ? GREEN : "white",
                      color: c.id === activeEmp.id ? "white" : "#334155",
                      border: "1px solid",
                      borderColor: c.id === activeEmp.id ? GREEN : BORDER,
                      fontSize: 12,
                    }}
                    onClick={() => setActiveEmp(c)}
                  >
                    <span>{getInitials(c.name)}</span>
                    <span className="font-medium">{c.name.split(" ").pop()}</span>
                  </div>
                  {i < chain.length - 1 && <span className="text-slate-300 text-xs">→</span>}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Full Org Tree */}
      <div style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 8, padding: 16 }}>
        {search.trim() ? (
          <div>
            <p className="text-sm font-medium text-slate-500 mb-4">
              Search results ({filteredEmps.length})
            </p>
            {filteredEmps.map((emp) => {
              const chain = buildChain(emp);
              return (
                <div key={emp.id} className="mb-3 p-3 rounded-md border bg-white hover:bg-slate-50 cursor-pointer transition-colors" style={{ borderColor: "#eef1ee" }} onClick={() => { setActiveEmp(emp); setSearch(""); }}>
                  <div className="flex items-center gap-3">
                    <EmployeeAvatar employee={emp} size={32} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900">{emp.name}</p>
                      <p className="text-xs text-slate-500">{emp.job_title || "—"}</p>
                    </div>
                  </div>
                  {chain.length > 1 && (
                    <div className="flex items-center gap-1 mt-2 ml-11 flex-wrap">
                      <span className="text-xs text-slate-400">Reports to:</span>
                      {chain.slice(0, -1).map((c, i) => (
                        <React.Fragment key={c.id}>
                          <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded">{c.name.split(" ").pop()}</span>
                          {i < chain.length - 2 && <span className="text-xs text-slate-300">→</span>}
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div>
            {roots.map((r) => (
              <OrgNode key={r.id} emp={r} employees={activeEmps} depth={0} activeId={activeEmp?.id} onSelect={setActiveEmp} />
            ))}
            {orphanRoots.map((r) => (
              <OrgNode key={r.id} emp={r} employees={activeEmps} depth={0} activeId={activeEmp?.id} onSelect={setActiveEmp} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanelOrgPage;